/**
 * Cookie handling for the Learnforce iframe embed (PENG-3889).
 *
 * Browsers key cookie storage on the pair (top-level site, frame origin), so an
 * embedded M:Suite gets a jar of its own rather than the one the first-party app
 * writes to. A cookie carrying no explicit SameSite is treated as Lax and is
 * refused outright in that jar, which is why the embedded app could neither read
 * an existing session nor write one of its own.
 */

const EPOCH = new Date(0)

/**
 * True when the app is rendered inside an iframe.
 *
 * Framing is fixed for the lifetime of the document, so callers are free to read
 * this once at module scope.
 */
const isEmbedded = () => window.self !== window.top

/**
 * Attributes a cookie needs in order to survive in a cross-site iframe.
 *
 * SameSite=None on its own is not enough. Chrome only admits a third-party cookie
 * into the partitioned jar when it is additionally marked Partitioned (CHIPS), and
 * it rejects Partitioned unless the cookie is also Secure. Verified against Chrome
 * 146: Partitioned coexists with Domain, so cookies scoped to a parent domain keep
 * being shared across subdomains — the partition key is the top-level *site*, which
 * does not change between www. and members. of the same domain.
 *
 * Returns nothing when not embedded, so first-party behaviour is left untouched.
 */
const crossSiteCookieOptions = () =>
  isEmbedded() ? { sameSite: 'none', secure: true, partitioned: true } : {}

/**
 * universal-cookie cannot write these cookies: it pins cookie@0.4.2, which predates
 * the Partitioned attribute and silently drops it. Bumping that dependency would
 * touch every cookie in the app, so writes that need Partitioned go through here
 * instead. Reads still go through universal-cookie — only serialization is the
 * problem, and its parser decodes what this encoder produces.
 */
const serializeCookie = (name, value, options = {}) => {
  const { path, domain, expires, maxAge, secure, sameSite, partitioned } = options
  let serialized = `${name}=${encodeURIComponent(value)}`

  if (domain) serialized += `; Domain=${domain}`
  if (path) serialized += `; Path=${path}`
  if (expires) serialized += `; Expires=${expires.toUTCString()}`
  if (maxAge !== undefined) serialized += `; Max-Age=${maxAge}`
  if (secure) serialized += '; Secure'
  if (sameSite) serialized += `; SameSite=${sameSite === 'none' ? 'None' : sameSite}`
  if (partitioned) serialized += '; Partitioned'

  return serialized
}

const writeCookie = (name, value, options) => {
  document.cookie = serializeCookie(name, value, options)
}

/**
 * A cookie is only expired by a write whose attributes match the ones it was
 * created with, so callers have to pass the same domain, path and cross-site
 * attributes they wrote it with.
 */
const expireCookie = (name, options = {}) => {
  document.cookie = serializeCookie(name, '', { ...options, expires: EPOCH, maxAge: 0 })
}

export {
  isEmbedded,
  crossSiteCookieOptions,
  serializeCookie,
  writeCookie,
  expireCookie
}
